import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { LoteService } from 'src/app/_services/lote/lote.service';
import { TableConfig } from 'src/app/interfaces/bl.interfaces';
interface infoLote {
  operationNumber: string;
  id: number;
}
interface infoSalida {
  id: number;
  tipoSalida: number | string;
}
/**
 * Componente de tabla reutilizable que renderiza datos según una configuración
 * (`TableConfig`), gestiona el ordenamiento por columnas, valida permisos de
 * navegación por módulo y emite eventos para las acciones de cada fila.
 */
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styles: [`
    .selected, .subtotal-row {
      background-color: #455660;
      color: white;
    }
    .hide-buttons {
      display: none;
    }
  `]
})
export class TableComponent implements OnChanges {
  @Input() table_config!: TableConfig;
  @Input() array: any = [];
  @Input() loader: boolean = false;
  infoLote: infoLote = {
    operationNumber: '',
    id: 0,
  };
  @Output() onInfoLote = new EventEmitter<infoLote>();
  @Output() onDetalleSalida = new EventEmitter<infoSalida>();
  @Output() onActualizarSalida = new EventEmitter<infoSalida>();
  @Output() onExportEntryDetail = new EventEmitter<infoSalida>();
  @Output() onExportEntryUpdate = new EventEmitter<infoSalida>();
  @Output() onDetalleEntrada = new EventEmitter<infoSalida>();
  @Output() onActualizarEntrada = new EventEmitter<infoSalida>();
  @Output() onEliminarSalida = new EventEmitter<infoSalida>();
  @Output() onDescargarSalida = new EventEmitter<infoSalida>();
  @Output() onPrintOutput = new EventEmitter<infoSalida>();
  @Output() onActualizarAbandono = new EventEmitter<number>();
  @Output() onEliminarAbandono = new EventEmitter<number>();
  @Output() onDescargarPedimento = new EventEmitter<number>();
  @Output() onDetalleEntryExport = new EventEmitter<number>();
  @Output() onActualizarEntryExport = new EventEmitter<number>();
  @Output() onEliminarEntryExport = new EventEmitter<number>();
  @Output() onDetailBill = new EventEmitter<number>();
  campoOrdenar: string = '';
  lastCampoOrdenar: string = 'No. barco';
  isAscending: boolean = false;
  groupByField: string = '';
  totalField: string = '';
  totalPiecesField: string = '';
  subtotalField: string = '';
  pathsWithValidation: string[] = [
    'batch-detail', 'batch-update', 'detail-batch-exportation', 'update-batch-exportation',
    'detail-batch-cabotage', 'update-batch-cabotage', 'appointments/detail', 'appointments/update'
  ];
  pathIndex: number = 0;
  isAuthorized: boolean = false;
  currentExportBatch: number = 0;
  currentCabotageBatch: number = 0;
  currentURL: string = '';
  @ViewChild('tabla', { static: false }) tabla: ElementRef | undefined;
  isScrollIntoItem: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private loteService: LoteService,
  ) {
    this.currentExportBatch = this.loteService.currentExportBatch;
    this.currentCabotageBatch = this.loteService.currentCabotageBatch;
    this.currentURL = this.router.url;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['table_config'] && changes['table_config'].firstChange) {
      this.groupByField = this.table_config.groupByField || '';
      this.totalField = this.table_config.totalField || '';
      this.totalPiecesField = this.table_config.totalPiecesField || '';
      this.subtotalField = this.table_config.subtotalField || '';
    }
    if (this.array.length && !this.loader && !this.isScrollIntoItem) {
      this.isScrollIntoItem = true;
      if (this.currentURL.includes('exportation/batch-list') && this.currentExportBatch) {
        setTimeout(() => {
          this.scrollIntoView();
        }, 1);
      }
      if (this.currentURL.includes('cabotage/batch-list') && this.currentCabotageBatch) {
        setTimeout(() => {
          this.scrollIntoView();
        }, 1);
      }
    }
  }

  /**
   * Navega a la ruta indicada validando los permisos del usuario cuando la ruta requiere autorización.
   * @param path - Ruta destino a la que se desea navegar.
   * @param id - Identificador del registro que se agrega a la ruta.
   */
  navigateTo(path: string, id: number) {
    if (this.searchPath(path)) {
      switch (this.pathIndex) {
        case 0:
          if (this.isValidPath('import', 'actions', 'consult')) {
            this.router.navigate([path, id]);
          }
          break;
        case 1:
          if (this.isValidPath('import', 'actions', 'update')) {
            this.router.navigate([path, id]);
          }
          break;
        case 2:
          if (this.isValidPath('export', 'actions', 'consult')) {
            this.router.navigate([path, id]);
          }
          break;
        case 3:
          if (this.isValidPath('export', 'actions', 'update')) {
            this.router.navigate([path, id]);
          }
          break;
        case 4:
          if (this.isValidPath('cabotage', 'actions', 'consult')) {
            this.router.navigate([path, id]);
          }
          break;
        case 5:
          if (this.isValidPath('cabotage', 'actions', 'update')) {
            this.router.navigate([path, id]);
          }
          break;
        case 6:
          if (this.isValidPath('appointments', 'appointment_request', 'consult')) {
            this.router.navigate([path, id]);
          }
          break;
        case 7:
          if (this.isValidPath('appointments', 'appointment_request', 'update')) {
            this.router.navigate([path, id]);
          }
          break;
        default:
          break;
      }
    } else {
      this.router.navigate([path, id]);
    }
  }

  /**
   * Determina si el perfil tiene permiso sobre la combinación módulo → menú → opción.
   * @param module - Clave del módulo a validar.
   * @param menu - Clave del menú a validar.
   * @param opcion - Acción u opción a validar.
   * @returns `true` si la acción está permitida; `false` si no lo está.
   */
  isValidPath(module: string, menu: string, opcion: string) {
    const auth = this.authService.getAuthorizationsByModule(module);
    if (auth[menu][opcion] === false) {
      this.isAuthorized = true;
      setTimeout(() => {
        this.isAuthorized = false;
      }, 3000);
      return false;
    }
    return true;
  }

  /**
   * Busca la ruta actual dentro de las rutas que requieren validación de permisos y guarda su índice.
   * @param currentPath - Ruta actual a evaluar.
   * @returns `true` si la ruta requiere validación; `false` en caso contrario.
   */
  searchPath(currentPath: string) {
    for (let i = 0; i < this.pathsWithValidation.length; i++) {
      if (currentPath.includes(this.pathsWithValidation[i])) {
        this.pathIndex = i;
        return true;
      }
    }
    return false;
  }

  /**
   * Emite la información del lote seleccionado (número de operación e identificador).
   * @param operationNumber - Número de operación del lote.
   * @param id - Identificador del lote.
   */
  ouputInfoLote(operationNumber: string, id: number) {
    this.infoLote = {
      operationNumber,
      id,
    };
    this.onInfoLote.emit(this.infoLote);
  }

  /**
   * Emite la solicitud de detalle de una salida.
   * @param id - Identificador de la salida.
   * @param tipoSalida - Tipo de salida.
   */
  outputDetalleSalida(id: number, tipoSalida: number) {
    this.onDetalleSalida.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de actualización de una salida.
   * @param id - Identificador de la salida.
   * @param tipoSalida - Tipo de salida.
   */
  outputActualizarSalida(id: number, tipoSalida: number) {
    this.onActualizarSalida.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de detalle de una entrada de exportación.
   * @param id - Identificador de la entrada.
   * @param tipoSalida - Tipo de salida asociado.
   */
  exportEntryDetail(id: number, tipoSalida: number) {
    this.onExportEntryDetail.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de actualización de una entrada de exportación.
   * @param id - Identificador de la entrada.
   * @param tipoSalida - Tipo de salida asociado.
   */
  exportUpdateDetail(id: number, tipoSalida: number) {
    this.onExportEntryUpdate.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de detalle de una entrada.
   * @param id - Identificador de la entrada.
   * @param tipoSalida - Tipo de salida asociado.
   */
  detalleEntrada(id: number, tipoSalida: number) {
    this.onDetalleEntrada.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de actualización de una entrada.
   * @param id - Identificador de la entrada.
   * @param tipoSalida - Tipo de salida asociado.
   */
  actualizarEntrada(id: number, tipoSalida: string) {
    this.onActualizarEntrada.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de eliminación de una salida.
   * @param id - Identificador de la salida.
   * @param tipoSalida - Tipo de salida.
   */
  outputEliminarSalida(id: number, tipoSalida: string) {
    this.onEliminarSalida.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de descarga de una salida.
   * @param id - Identificador de la salida.
   * @param tipoSalida - Tipo de salida.
   */
  outputDescargarSalida(id: number, tipoSalida: string) {
    this.onDescargarSalida.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de impresión de una salida.
   * @param id - Identificador de la salida.
   * @param tipoSalida - Tipo de salida.
   */
  outputPrintOutput(id: number, tipoSalida: string) {
    this.onPrintOutput.emit({
      id,
      tipoSalida,
    });
  }

  /**
   * Emite la solicitud de actualización de un abandono.
   * @param id - Identificador del abandono.
   */
  outputActualizarAbandono(id: number) {
    this.onActualizarAbandono.emit(id);
  }

  /**
   * Emite la solicitud de eliminación de un abandono.
   * @param id - Identificador del abandono.
   */
  outputEliminarAbandono(id: number) {
    this.onEliminarAbandono.emit(id);
  }

  /**
   * Emite la solicitud de descarga de un pedimento.
   * @param id - Identificador del pedimento.
   */
  outputDescargarPedimento(id: number) {
    this.onDescargarPedimento.emit(id);
  }

  /**
   * Emite la solicitud de detalle de una entrada de exportación (entry export).
   * @param id - Identificador de la entrada de exportación.
   */
  outputDetalleEntryExport(id: number) {
    this.onDetalleEntryExport.emit(id);
  }

  /**
   * Emite la solicitud de actualización de una entrada de exportación (entry export).
   * @param id - Identificador de la entrada de exportación.
   */
  outputActualizarEntryExport(id: number) {
    this.onActualizarEntryExport.emit(id);
  }

  /**
   * Emite la solicitud de eliminación de una entrada de exportación (entry export).
   * @param id - Identificador de la entrada de exportación.
   */
  outputEliminarEntryExport(id: number) {
    this.onEliminarEntryExport.emit(id);
  }

  /**
   * Emite la solicitud de detalle de una factura.
   * @param id - Identificador de la factura.
   */
  outputDetailBill(id: number) {
    this.onDetailBill.emit(id);
  }

  /**
   * Establece el campo de ordenamiento y alterna la dirección cuando se repite la misma columna.
   * @param campo - Nombre del campo por el cual ordenar.
   */
  ordenar(campo: string) {
    if (this.lastCampoOrdenar === campo) {
      this.isAscending = !this.isAscending;
    } else {
      this.isAscending = false;
    }
    this.lastCampoOrdenar = campo;
    this.campoOrdenar = campo;
  }

  /**
   * Devuelve el color asociado a un estado textual general.
   * @param status - Estado a evaluar.
   * @returns El nombre del color correspondiente al estado.
   */
  seleccionarColor(status: string) {
    switch (status) {
      case 'Pendiente':
        return 'orange';
      case 'Enviado':
        return 'green';
      case 'Rechazado':
        return 'red';
      case 'No Registrado':
        return 'gray';
      case 'Sin registro':
        return 'gray';
      default:
        return 'gray';
    }
  }

  /**
   * Devuelve el color asociado al estado de un abandono.
   * @param status - Código numérico del estado de abandono.
   * @returns El nombre del color correspondiente.
   */
  seleccionarColorAbandono(status: number) {
    switch (status) {
      case 1:
        return 'green';
      case 2:
        return 'orange';
      case 3:
        return 'red';
      default:
        return 'green';
    }
  }

  /**
   * Devuelve el color asociado al estado de un pedimento.
   * @param status - `true` si el pedimento está pendiente/incompleto.
   * @returns `'red'` cuando `status` es verdadero; de lo contrario `'green'`.
   */
  seleccionarColorPedimento(status: boolean) {
    return status ? 'red' : 'green';
  }

  /**
   * Devuelve el color asociado al estado de sincronización.
   * @param status - `true` si está sincronizado.
   * @returns `'green'` cuando `status` es verdadero; de lo contrario `'red'`.
   */
  seleccionarColorSincronizacion(status: boolean) {
    return status ? 'green' : 'red';
  }

  /**
   * Devuelve el color asociado al estado de una factura.
   * @param status - `true` si la factura está completa.
   * @returns `'green'` cuando `status` es verdadero; de lo contrario `'orange'`.
   */
  seleccionarColorFactura(status: boolean) {
    return status ? 'green' : 'orange';
  }

  /**
   * Devuelve el color asociado al estado de una entrada.
   * @param status - Código numérico del estado de entrada.
   * @returns El nombre del color correspondiente.
   */
  seleccionarColorEntrada(status: number) {
    switch (status) {
      case 1:
        return 'orange';
      case 2:
        return 'green';
      case 3:
        return 'red';
      case 4:
        return 'gray';
      default:
        return 'gray';
    }
  }

  /**
   * Devuelve el color asociado al estado de una cita.
   * @param status - Estado textual de la cita.
   * @returns El nombre del color correspondiente.
   */
  appointmentColor(status: string) {
    switch (status) {
      case 'Pendiente':
        return 'gray';
      case 'No aceptada':
        return 'red';
      case 'Cancelada':
        return 'red';
      case 'No enviada':
        return 'orange';
      case 'Aceptada':
        return 'green';
      case 'Enviada':
        return 'green';
      default:
        return 'gray';
    }
  }

  /**
   * Determina la clase CSS de resaltado para una fila según la ruta actual y el estado del registro.
   * @param item - Registro de la fila a evaluar.
   * @returns La clase CSS de resaltado, o cadena vacía si no aplica.
   */
  selectLastItem(item: any) {
    if (this.currentURL.includes('exportation/batch-list')) {
      if (this.currentExportBatch === item.registry_number) {
        return 'selected';
      }
    }
    if (this.currentURL.includes('cabotage/batch-list')) {
      if (this.currentCabotageBatch === item.registry_number) {
        return 'selected';
      }
    }
    if (this.currentURL.includes('scale-ticket/list') && item) {
      // Si la boleta no esta cancelada
      if (item.status_ticket === true) {
        // Si la boleta es de koura, no esta completa y no ha sido enviada a koura
        if (item.customer_id === 11 && item.routes_int_exp === 2 && item.status === false && item.status_koura_data_sending === false) {
          return 'row-missing';
        }
        // Si la boleta es de koura, esta completa y no ha sido enviada a koura
        if (item.customer_id === 11 && item.routes_int_exp === 2 && item.status === true && item.status_koura_data_sending === false) {
          return 'row-pending';
        }
        // Si la boleta es de koura, esta completa y ha sido enviada a koura
        if (item.customer_id === 11 && item.routes_int_exp === 2 && item.status === true && item.status_koura_data_sending === true) {
          return 'row-sended';
        }
      }
    }
    if (this.currentURL.includes('exportation/batch-movement/entry-list') && item) {
      // Si la entrada de exportacion ya fue asignada a un barco
      if (item.is_assigned) {
        return 'row-sended';
      }
    }
    return '';
  }

  /**
   * Determina la clase CSS para las filas de subtotal.
   * @param item - Registro de la fila a evaluar.
   * @returns `'subtotal-row'` si la fila es de subtotal; de lo contrario cadena vacía.
   */
  subtotalRow(item: any) {
    const withSubtotal = item.subtototalRow;
    return withSubtotal ? 'subtotal-row' : '';
  }

  /**
   * Genera el identificador HTML de una fila según la ruta actual y el número de registro.
   * @param item - Registro de la fila a evaluar.
   * @returns El identificador de la fila (por ejemplo `row-123`), o cadena vacía si no aplica.
   */
  assignId(item: any) {
    if (this.currentURL.includes('exportation/batch-list')) {
      return `row-${item.registry_number}`;
    } else if (this.currentURL.includes('cabotage/batch-list')) {
      return `row-${item.registry_number}`;
    } else {
      return '';
    }
  }

  /**
   * Desplaza la vista hasta la fila del lote actualmente seleccionado, cuando existe.
   */
  scrollIntoView() {
    const tagId: number = this.currentURL.includes('exportation/batch-list') ?
      this.currentExportBatch :
      this.currentURL.includes('cabotage/batch-list') ?
      this.currentCabotageBatch : 0;
    if (this.tabla && tagId) {
      const element = this.tabla.nativeElement.querySelector(`#row-${tagId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
}
